import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
// Firestore imports removed
import { ApiService } from "../../services/api_service";
import { formatFirestoreDate } from "../../utils/date_formatter";
import { convertTo12HourFormat } from "../../utils/time_formatter";
import Navbar from "../../common_components/navbar/Navbar";
import { format } from 'date-fns';

const SelectTickets = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  // State Management
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection State
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Data Lists State
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableTickets, setAvailableTickets] = useState([]);

  // Loading States for sub-sections
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(false);

  // Cart State
  const [quantities, setQuantities] = useState({});
  const [expandedTicketId, setExpandedTicketId] = useState(null);

  // Helpers
  const formatINR = (amount) => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '0';
    try {
      return Number(amount).toLocaleString('en-IN');
    } catch (_) {
      return String(amount);
    }
  };

  const getDateParts = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return {
      day: format(d, 'dd'),
      weekday: format(d, 'EEE'),
      month: format(d, 'MMM').toUpperCase(),
    };
  };

  const getTime = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return format(d, 'hh:mm a');
  };

  const getEventDateTime = () => {
    if (!event) return '';
    // Use first date and slot for display if available, purely cosmetic fallback
    if (availableDates.length > 0 && availableSlots.length > 0) {
      const d = new Date(availableDates[0].dateOfEvent);
      const start = new Date(availableSlots[0].startTime);
      const end = new Date(availableSlots[0].endTime);
      return `${format(d, 'dd MMM yyyy')} | ${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
    }
    return '';
  };

  // Initial Fetch: Event + Dates
  useEffect(() => {
    let isMounted = true;
    async function fetchEventAndDates() {
      try {
        setLoading(true);
        // Parallel fetch for Event and Dates
        const [eventRes, datesRes] = await Promise.all([
          ApiService.getSingleEvent(eventId),
          ApiService.getEventDates(eventId)
        ]);

        if (!isMounted) return;

        const eventData = eventRes.data || eventRes;
        const datesList = datesRes.data || datesRes || [];

        if (eventData) {
          setEvent(eventData);
          // Sort dates
          datesList.sort((a, b) => new Date(a.dateOfEvent) - new Date(b.dateOfEvent));
          setAvailableDates(datesList);

          // Auto-select first date
          if (datesList.length > 0) {
            handleDateChange(datesList[0]);
          }
        } else {
          setError("Event not found");
        }
      } catch (err) {
        console.error("Error fetching event data:", err);
        if (isMounted) setError("Failed to load event data");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchEventAndDates();

    return () => { isMounted = false; };
  }, [eventId]);

  // Handle Date Change -> Fetch Slots
  const handleDateChange = async (dateObj) => {
    setSelectedDate(dateObj);
    // Reset downstream selections
    setSelectedSlot(null);
    setAvailableSlots([]);
    setAvailableTickets([]);
    setQuantities({});

    try {
      setLoadingSlots(true);
      const slotsRes = await ApiService.getEventSlots(eventId, dateObj.id);
      const slotsList = slotsRes.data || slotsRes || [];

      // Sort slots
      slotsList.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setAvailableSlots(slotsList);

      // Auto-select first slot
      if (slotsList.length > 0) {
        handleSlotChange(slotsList[0], dateObj); // Pass dateObj to ensure we use the correct context
      }
    } catch (e) {
      console.error("Error fetching slots:", e);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Handle Slot Change -> Fetch Tickets
  const handleSlotChange = async (slotObj, dateContext = selectedDate) => {
    setSelectedSlot(slotObj);
    setAvailableTickets([]);
    setQuantities({});

    if (!dateContext) return;

    try {
      setLoadingTickets(true);
      const ticketsRes = await ApiService.getEventTickets(eventId, dateContext.id, slotObj.id);
      const ticketsList = ticketsRes.data || ticketsRes || [];
      setAvailableTickets(ticketsList);
    } catch (e) {
      console.error("Error fetching tickets:", e);
    } finally {
      setLoadingTickets(false);
    }
  };


  // Cart Logic
  const handleAdd = (ticketId) => {
    if (!selectedSlot) return;
    const t = availableTickets.find((tk) => tk.id === ticketId);
    const remaining = Math.max(0, t?.availableTickets || 0);
    if (remaining <= 0) return;
    setQuantities({ [ticketId]: Math.min(1, remaining) });
  };

  const handleIncrement = (ticketId) => {
    if (!selectedSlot) return;
    const t = availableTickets.find((tk) => tk.id === ticketId);
    if (!t) return;
    const remaining = Math.max(0, t?.availableTickets || 0);
    const personAllowed = t?.personAllowedPerTicket || 10;
    const systemLimit = 10;
    const maxPerBooking = Math.min(personAllowed, systemLimit, remaining);
    setQuantities((prev) => ({
      ...prev,
      [ticketId]: Math.min((prev[ticketId] || 0) + 1, maxPerBooking),
    }));
  };

  const handleDecrement = (ticketId) => {
    setQuantities((prev) => {
      const updated = { ...prev, [ticketId]: prev[ticketId] - 1 };
      if (updated[ticketId] <= 0) {
        const { [ticketId]: removed, ...rest } = updated;
        return rest;
      }
      return updated;
    });
  };

  const totalQuantity = Object.values(quantities).reduce((a, b) => a + b, 0);
  const totalPrice = availableTickets.reduce((acc, ticket) => {
    const qty = quantities[ticket.id] || 0;
    return acc + (ticket.price * qty);
  }, 0);

  if (loading) return <div className="bg-[#111] text-white p-10 text-center font-poppins mt-40">Loading...</div>;
  if (error) return <div className="bg-[#111] text-red-400 p-10 text-center font-poppins mt-40">{error}</div>;
  if (!event) return <div className="bg-[#111] text-red-400 p-10 text-center font-poppins mt-40">Event data not available</div>;

  return (
    <>
      <Navbar hideBorder />
      {/* Event Header Bar */}
      <div className="w-full flex justify-center px-2 sm:px-4 md:px-6 py-4 sm:py-6 bg-[#000] pt-[85px] sm:pt-[88px] lg:pt-[92px]">
        <div className="flex flex-row items-center gap-2 sm:gap-3 md:gap-4">
          <button
            className="text-white text-lg sm:text-xl px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-[#232323] rounded-lg focus:outline-none transition-colors flex items-center justify-center"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <div className="flex flex-col items-center justify-center min-w-0 px-2 sm:px-3 md:px-4">
            <div className="truncate text-center text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-white leading-tight">
              {event.name}
            </div>
            <div className="truncate text-center text-xs sm:text-sm text-gray-300 max-w-[200px] sm:max-w-xs md:max-w-md mt-1">
              {event.eventLocation?.locationText}
            </div>
            <div className="truncate text-center text-[10px] sm:text-xs text-gray-400 mt-1">
              {getEventDateTime()}
            </div>
          </div>
          <button
            className="text-white text-lg sm:text-xl px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-[#232323] rounded-lg focus:outline-none transition-colors"
            onClick={() => navigate('/')}
            aria-label="Close"
          >
            &#10005;
          </button>
        </div>
      </div>

      <div className="min-h-screen bg-[#111] text-white p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">

          <div className="mb-8 sm:mb-10 md:mb-12">
            <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-5 md:mb-6">Select Date & Time</h3>

            {/* Dates */}
            <div className="flex gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-5 md:mb-6 flex-wrap">
              {availableDates.map((ticketDate, idx) => {
                const { day, weekday, month } = getDateParts(ticketDate.dateOfEvent);
                const isSelected = selectedDate && selectedDate.id === ticketDate.id;
                return (
                  <button
                    key={ticketDate.id || idx}
                    className={`flex flex-col items-center rounded-lg border transition-all duration-200 focus:outline-none font-medium text-center w-12 sm:w-14 md:w-16 overflow-hidden
                      ${isSelected ? 'border-white shadow' : 'border-white'}`}
                    onClick={() => handleDateChange(ticketDate)}
                  >
                    <div className={`w-full px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 ${isSelected ? 'bg-white text-black' : 'bg-[#111] text-white'}`}>
                      <span className="text-base sm:text-lg md:text-xl font-bold leading-none block">{day}</span>
                      <span className="text-[10px] sm:text-xs uppercase tracking-wider block">{weekday}</span>
                    </div>
                    <div className="w-full px-2 sm:px-3 md:px-4 py-1 bg-[#3B3B3B] text-gray-400">
                      <span className="text-[10px] sm:text-xs">{month}</span>
                    </div>
                  </button>
                );
              })}
              {availableDates.length === 0 && <div className="text-gray-400">No dates available</div>}
            </div>

            {/* Slots */}
            {loadingSlots ? (
              <div className="text-gray-400 text-sm">Loading slots...</div>
            ) : (
              <div className="flex gap-2 sm:gap-3 flex-wrap">
                {availableSlots.map((slot, idx) => {
                  const isSelected = selectedSlot && selectedSlot.id === slot.id;
                  return (
                    <button
                      key={slot.id || idx}
                      className={`px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 rounded-md border border-[#111] text-xs sm:text-sm font-medium transition-all duration-200 focus:outline-none
                        ${isSelected ? 'bg-white text-black shadow' : 'bg-[#232323] text-white hover:bg-gray-800'}`}
                      onClick={() => handleSlotChange(slot)}
                    >
                      {getTime(slot.startTime)}
                    </button>
                  );
                })}
                {selectedDate && availableSlots.length === 0 && <div className="text-gray-400 text-sm">No slots available for this date</div>}
              </div>
            )}
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Select Tickets</h3>

            {loadingTickets ? (
              <div className="p-10 text-center text-gray-400">Loading tickets...</div>
            ) : availableTickets.length > 0 ? (
              availableTickets.map((ticket) => {
                const isAvailable = ticket.isLive && ticket.availableTickets > 0;
                const maxPerBooking = Math.min(ticket.personAllowedPerTicket || 10, 10, ticket.availableTickets || 0);
                const isExpanded = expandedTicketId === ticket.id;
                const isSelected = quantities[ticket.id] && quantities[ticket.id] > 0;
                const remaining = Math.max(0, ticket.availableTickets || 0);

                return (
                  <div className={`bg-[#202020] rounded-xl p-6 mb-6 flex flex-col md:flex-row md:items-start justify-between relative shadow-sm border border-[#333] ${isSelected ? 'ring-2 ring-[#17BD3B] ring-opacity-40' : ''}`} key={ticket.id}>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-[16px] leading-[24px] font-medium text-white">{ticket.name}</h4>
                        {isAvailable && remaining > 0 && !isSelected && (
                          <button
                            className="bg-[#17BD3B] hover:bg-[#15a834] text-white font-medium px-5 py-2 rounded-lg transition-colors text-sm"
                            onClick={() => handleAdd(ticket.id)}
                            disabled={remaining === 0}
                          >
                            Select Ticket
                          </button>
                        )}
                        {(!isAvailable || remaining === 0) && !isSelected && (
                          <button className="bg-gray-700 text-gray-400 font-medium px-5 py-2 rounded-lg text-sm cursor-not-allowed" disabled>
                            Sold Out
                          </button>
                        )}
                        {isSelected && (
                          <span className="text-[#17BD3B] text-sm font-medium">Selected</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[20px] leading-[30px] font-semibold text-white">₹ {formatINR(ticket.price)}</span>
                        <span className={`text-[14px] leading-[21px] font-medium ${isAvailable && remaining > 0 ? 'text-[#17BD3B]' : 'text-red-400'}`}>
                          {isAvailable && remaining > 0 ? 'Available' : 'Sold Out'}
                        </span>
                        <span className="text-[12px] leading-[18px] text-[#B3B3B3]">
                          • Max {maxPerBooking} ticket{maxPerBooking !== 1 ? 's' : ''} per booking
                        </span>
                      </div>
                      {ticket.benefits && ticket.benefits.length > 0 && (
                        <ul className="list-disc pl-5 text-[#B3B3B3] text-[12px] leading-[18px] mb-2">
                          {ticket.benefits.map((benefit, i) => (
                            <li key={i}>{benefit}</li>
                          ))}
                        </ul>
                      )}
                      <button
                        className="text-white text-xs flex items-center gap-1 mt-2 focus:outline-none hover:text-[#17BD3B]"
                        onClick={() => setExpandedTicketId(isExpanded ? null : ticket.id)}
                      >
                        {isExpanded ? 'Show less' : 'Show more'}
                        <span className={`inline-block transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="mt-3 bg-[#1a1a1a] rounded-lg p-4 text-[12px] leading-[18px] text-[#B3B3B3] shadow-inner border border-[#333]">
                          <div className="text-white font-medium mb-1">Description</div>
                          <div>{ticket.description}</div>
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <div className="flex items-center space-x-3 mt-4 md:mt-0 md:ml-4">
                        <button
                          onClick={() => handleDecrement(ticket.id)}
                          className="w-10 h-10 bg-[#202020] border border-[#444] text-white rounded-lg hover:bg-[#2a2a2a] transition-colors text-xl"
                        >
                          -
                        </button>
                        <span className="text-xl font-semibold min-w-[2rem] text-center">{quantities[ticket.id]}</span>
                        <button
                          onClick={() => handleIncrement(ticket.id)}
                          className="w-10 h-10 bg-[#202020] border border-[#444] text-white rounded-lg hover:bg-[#2a2a2a] transition-colors text-xl disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={quantities[ticket.id] >= maxPerBooking}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="bg-[#232323] rounded-xl p-6 text-center">
                <p className="text-gray-300">
                  {loadingTickets ? "" : "No slots selected or no tickets available."}
                </p>
              </div>
            )}
          </div>

          {totalQuantity > 0 && (
            <div className="fixed bottom-0 left-0 right-0 bg-[#232323] border-t border-gray-700 p-4 z-50">
              <div className="max-w-4xl mx-auto flex items-center justify-between">
                <div>
                  <strong className="text-xl">₹{formatINR(totalPrice)}</strong>{" "}
                  <span className="text-gray-300">
                    | {totalQuantity} Ticket{totalQuantity > 1 ? "s" : ""}
                  </span>
                </div>
                <Link
                  to="/checkout-tickets"
                  state={{
                    eventId: eventId,
                    selectedDate: selectedDate,
                    selectedSlot: { ...selectedSlot, tickets: availableTickets }, // Pass tickets within slot for compatibility
                    selectedTickets: quantities,
                    totalPrice: totalPrice,
                    totalTickets: totalQuantity,
                    eventName: event.name,
                    eventLocation: event.eventLocation?.locationText,
                    eventData: event,
                  }}
                >
                  <button className="bg-[#17BD3B] text-white px-8 py-3 rounded-lg hover:bg-[#15a834] transition-colors font-semibold">
                    Checkout
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SelectTickets;
